import type { Pagination, Transaction } from "@/apis/gql/types";
import type { ServerFilters } from "@/hooks/useQueryFilters";
import { useTenant } from "@/providers/session";
import { gql, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useSpinDelay } from "spin-delay";

export const useGetAllTransactions = ({
  query,
  page,
  limit,
  offset,
  global = "",
  sort = "bookingDate",
  order = "desc",
}: {
  query: ServerFilters;
  page: number;
  limit: number;
  offset: number;
  global?: string;
  sort?: string;
  order?: string;
}) => {
  const { tenantId } = useTenant();

  const memoizedQuery = useMemo(() => query, [query]);

  const [pagination, setPagination] = useState<Pagination>({
    currentPage: page,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: limit,
    hasNextPage: false,
    hasPreviousPage: false,
    nextPage: page,
    previousPage: page > 1 ? page - 1 : 1,
  });

  const { data, loading, error } = useQuery<{
    transactions: {
      data: Transaction[];
      pagination: Pagination;
    };
  }>(GET_ALL_TRANSACTIONS, {
    variables: {
      tenantId,
      query: memoizedQuery,
      page,
      limit,
      offset,
      global,
      sort,
      order,
    },
    notifyOnNetworkStatusChange: false,
  });

  useEffect(() => {
    if (data?.transactions?.pagination && !loading) {
      setPagination(data.transactions.pagination);
    }
  }, [data?.transactions?.pagination, loading]);

  const isLoading = useSpinDelay(loading, { delay: 600, minDuration: 400 });

  return {
    data: data?.transactions?.data ?? [],
    pagination,
    loading: isLoading,
    error,
  };
};

export const GET_ALL_TRANSACTIONS = gql`
  query GetAllTransactions(
    $tenantId: String
    $page: Int
    $limit: Int
    $offset: Int
    $query: JSON
    $global: String
    $sort: String
    $order: String
  ) {
    transactions(
      tenantId: $tenantId
      page: $page
      limit: $limit
      offset: $offset
      query: $query
      global: $global
      sort: $sort
      order: $order
    ) {
      data {
        bankTxnId
        tenantId
        status
        amount
        currency
        createdAt
        counterpartyName
        counterpartyIban
        purpose
        executionDate
        side
        bookingDate
        smbAttachmentStatus
        originalAmount
        originalCurrency
        invoices {
          invoiceId
          invoiceNo
          totalAmount
          currency
          supplierName
          invoiceDate
          status
          invoiceType
          matchedAt
          journalEntries {
            journalEntryId
            journalLines {
              journalLineId
              journalEntryId
              journalAccountId
              taxCodeId
              costCenterId
              debit
              credit
              currency
              originalCurrency
              originalAmount
              number
              tenantId
              createdAt
              updatedAt
              deletedAt
            }
          }
        }
        journalEntries {
          journalEntryId
          timestamp
          status
          documentNumber
          bookingText
          number
          createdBy
          journalLines {
            journalLineId
            debit
            credit
            currency
             taxCode {
              taxCodeId
              datevBu
            }
            journalAccount {
              journalAccountId
              number
              name
              accountType
              entityType
            }
          }
        }
        bankAccount {
          bankAccountId
          name
          accountNumber
          type
          iban
          defaultAccount
          currency
        }
        bankConnection {
          bankConnectionId
          metadata {
            id
            bank {
              name
              logo {
                url
              }
              icon {
                url
              }
              bankGroup {
                name
                icon {
                  url
                }
                logo {
                  url
                }
              }
            }
          }
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
        nextPage
        previousPage
      }
    }
  }
`;
